import cls from "classnames";
import { ForwardedRef, InputHTMLAttributes, ReactNode, forwardRef, useId } from "react";

import FieldError from "components/ui/FieldError";
import styles from "./index.module.scss";

type Props = {
  // "boxed" is the form control; "pill" is the rounded search-and-filter look.
  variant?: "boxed" | "pill";
  // Control height, replacing the native `size` attribute, which counts characters.
  // Each step matches the buttons and selects it shares a row with; "extra-large"
  // is the roomy form field and "small" suits dense filter bars.
  size?: "small" | "medium" | "large" | "extra-large";
  // Floats inside the control: centred while the field is empty and unfocused,
  // and up to the top once it has a value or the caret. Use instead of wrapping
  // the control in a FormRow, not as well.
  label?: ReactNode;
  // Validation message under the control. Implies `invalid`, and is announced
  // with the field.
  error?: ReactNode;
  // Rendered before the input — a search glyph, a currency symbol.
  leading?: ReactNode;
  // Rendered after the input — a clear button, a unit, a spinner.
  trailing?: ReactNode;
  // Error border without a message of its own.
  invalid?: boolean;
  // className and style both land on the outer element, which callers size and
  // place; the bordered control fills it.
  className?: string;
} & Omit<InputHTMLAttributes<HTMLInputElement>, "size">;

/**
 * The app's text input. The control carries the border and focus ring so the
 * leading and trailing slots sit inside it, and the input owns the vertical
 * padding so a click anywhere down the control reaches it.
 *
 * Chrome a call site can restate, on the root: --field-border-width,
 * --field-border-color, --field-bg, --field-weight and --field-placeholder-style.
 *
 * The ref lands on the inner <input>, so integration layers that need the real
 * control (react-autosuggest's renderInputComponent contract, imperative
 * focus()) can treat this as the input it wraps.
 */
function TextInput(
  {
    variant = "boxed",
    size = "medium",
    label,
    error,
    leading,
    trailing,
    invalid,
    className,
    style,
    id,
    placeholder,
    "aria-describedby": callerDescribedBy,
    "aria-invalid": callerInvalid,
    ...rest
  }: Props,
  ref: ForwardedRef<HTMLInputElement>
) {
  const generatedId = useId();
  const messageId = useId();
  const controlId = id ?? generatedId;
  // A FormRow above may describe the same input with its own hint, so the two
  // ids join rather than one replacing the other.
  const describedBy =
    [callerDescribedBy, error ? messageId : undefined].filter(Boolean).join(" ") ||
    undefined;

  return (
    <div className={cls(styles.root, styles[variant], className)} style={style}>
      <div
        className={cls(
          styles.field,
          styles[size],
          label && styles.labelled,
          (invalid || error) && styles.invalid,
          "talaera-text-input"
        )}
      >
        {leading}
        <input
          {...rest}
          // Marks the control as already styled by the kit, so the legacy
          // dialog form-control block leaves it alone.
          className="talaera-control"
          ref={ref}
          id={controlId}
          // A space keeps :placeholder-shown meaningful, which is how the label
          // knows the field is empty. The real placeholder only paints on focus.
          placeholder={placeholder ?? " "}
          aria-invalid={invalid || error ? true : callerInvalid}
          aria-describedby={describedBy}
        />
        {label && (
          <label className={styles.label} htmlFor={controlId}>
            {label}
          </label>
        )}
        {trailing}
        {label && (
          /* Carries the border so the legend can cut a gap for the floated
             label. Decorative: the visible label is the one above. */
          <fieldset aria-hidden className={styles.notch}>
            <legend className={styles.legend}>
              <span>{label}</span>
            </legend>
          </fieldset>
        )}
      </div>
      {error && <FieldError id={messageId}>{error}</FieldError>}
    </div>
  );
}

export default forwardRef(TextInput);
